import React, { createContext, useState, useEffect, ReactNode } from "react";
import { useConfig } from "./ConfigContext";
import { Theme } from "../types";

interface ThemeContextProps {
  theme: string;
  setTheme: (theme: string) => void;
  availableThemes: Theme[];
}

export const ThemeContext = createContext<ThemeContextProps>({
  theme: "theme-dark",
  setTheme: () => { },
  availableThemes: [],
});

// 🔍 Utility: Extract themes from loaded CSS rules
function getAvailableThemesFromCSS(): Theme[] {
  const themes: Theme[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = (sheet as CSSStyleSheet).cssRules;
    } catch {
      continue; // ignore CORS-protected sheets
    }
    for (const rule of Array.from(rules)) {
      if (rule instanceof CSSStyleRule && rule.selectorText.startsWith("body.theme-")) {
        const className = rule.selectorText.replace("body.", "").trim();
        if (!themes.find((t) => t.className === className)) {
          // crude display name: strip prefix and format nicely
          const name = className
            .replace("theme-", "")
            .replace(/-/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
          themes.push({ name, className });
        }
      }
    }
  }
  return themes;
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { config, loading } = useConfig(); // 🔥 wait for config
  const [theme, setTheme] = useState(() => {
    // Use sessionStorage for instant theme on reload (avoids white flash)
    return sessionStorage.getItem("theme") || "theme-dark";
  });
  const [availableThemes, setAvailableThemes] = useState<Theme[]>([]);

  // only update when config finishes
  useEffect(() => {
    if (!loading && config?.theme) {
      setTheme(config.theme);
    }
  }, [loading, config?.theme]);

  useEffect(() => {
    const foundThemes = getAvailableThemesFromCSS();
    setAvailableThemes(foundThemes);

    foundThemes.forEach((t) => document.body.classList.remove(t.className));
    document.body.classList.add(theme);
    sessionStorage.setItem("theme", theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, availableThemes }}>
      {children}
    </ThemeContext.Provider>
  );
};
